import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ArrowRight, X, ZoomIn, ZoomOut,
  Share2, Heart, Copy, Check, Mail, ShoppingBag, MessageSquare
} from 'lucide-react';
import { artworks, artistInfo, collections } from '../data/artworksData';

const ArtworkDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';

  // Find artwork
  const artworkIndex = artworks.findIndex(a => a.id === parseInt(id));
  const artwork = artworks[artworkIndex];

  // States
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);

  // Navigation
  const prevArtwork = artworkIndex > 0 ? artworks[artworkIndex - 1] : null;
  const nextArtwork = artworkIndex < artworks.length - 1 ? artworks[artworkIndex + 1] : null;

  // Related artworks (same series or category)
  const relatedArtworks = artworks
    .filter(a =>
      a.id !== artwork?.id &&
      (a.series === artwork?.series || a.category === artwork?.category)
    )
    .slice(0, 4);

  // Get series info
  const series = collections.find(c => c.id === artwork?.series);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && prevArtwork) {
        navigate(`/artwork/${prevArtwork.id}`);
      } else if (e.key === 'ArrowRight' && nextArtwork) {
        navigate(`/artwork/${nextArtwork.id}`);
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevArtwork, nextArtwork, navigate, isFullscreen]);

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.5, 1));

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gallery-muted">
          {lang === 'ru' ? 'Работа не найдена' : 'Artwork not found'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gallery-bg pt-20">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gallery-border">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gallery-muted hover:text-gallery-text">
                {lang === 'ru' ? 'Главная' : 'Home'}
              </Link>
              <span className="text-gallery-muted">/</span>
              <Link to="/gallery" className="text-gallery-muted hover:text-gallery-text">
                {lang === 'ru' ? 'Галерея' : 'Gallery'}
              </Link>
              <span className="text-gallery-muted">/</span>
              <span className="text-gallery-text">{artwork.title[lang]}</span>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Image Section */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div
                className="relative bg-gallery-light overflow-hidden cursor-zoom-in"
                onClick={() => setIsFullscreen(true)}
              >
                <div className="aspect-[4/5]">
                  <img
                    src={artwork.image}
                    alt={artwork.title[lang]}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Status Badge */}
                {artwork.status !== 'available' && (
                  <div className="absolute top-4 right-4">
                    <span className={`px-4 py-2 text-sm tracking-wider uppercase ${
                      artwork.status === 'sold'
                        ? 'bg-gallery-text text-white'
                        : 'bg-gallery-accent text-white'
                    }`}>
                      {artwork.status === 'sold'
                        ? (lang === 'ru' ? 'Продано' : 'Sold')
                        : (lang === 'ru' ? 'Зарезервировано' : 'Reserved')
                      }
                    </span>
                  </div>
                )}

                {/* Zoom Hint */}
                <div className="absolute bottom-4 right-4">
                  <span className="flex items-center gap-2 px-3 py-2 bg-white/90 text-gallery-text text-xs">
                    <ZoomIn size={14} />
                    {lang === 'ru' ? 'Увеличить' : 'Zoom'}
                  </span>
                </div>
              </div>

              {/* Thumbnail Navigation (placeholder for multiple images) */}
              {artwork.images && artwork.images.length > 1 && (
                <div className="flex gap-2 mt-4">
                  {artwork.images.map((img, idx) => (
                    <div key={idx} className="w-20 h-20 bg-gallery-light">
                      <img
                        src={img}
                        alt=""
                        className="w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:sticky lg:top-32 lg:self-start"
            >
              {/* Title & Year */}
              <div className="mb-8">
                <p className="text-gallery-muted text-sm tracking-wider uppercase mb-2">
                  {artwork.year}
                </p>
                <h1 className="font-serif text-3xl md:text-4xl text-gallery-text mb-4">
                  {artwork.title[lang]}
                </h1>
                <p className="text-gallery-muted">
                  {artistInfo.name[lang]}
                </p>
              </div>

              {/* Details */}
              <div className="space-y-4 mb-8 pb-8 border-b border-gallery-border">
                <div className="flex justify-between">
                  <span className="text-gallery-muted">
                    {lang === 'ru' ? 'Техника' : 'Technique'}
                  </span>
                  <span className="text-gallery-text text-right">
                    {artwork.technique[lang]}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gallery-muted">
                    {lang === 'ru' ? 'Размер' : 'Size'}
                  </span>
                  <span className="text-gallery-text">
                    {artwork.size}
                  </span>
                </div>
                {series && (
                  <div className="flex justify-between">
                    <span className="text-gallery-muted">
                      {lang === 'ru' ? 'Серия' : 'Series'}
                    </span>
                    <Link
                      to={`/gallery?series=${series.id}`}
                      className="text-gallery-accent hover:underline"
                    >
                      {series.title[lang]}
                    </Link>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gallery-muted">
                    {lang === 'ru' ? 'Уникальность' : 'Uniqueness'}
                  </span>
                  <span className="text-gallery-text">
                    {artwork.unique
                      ? (lang === 'ru' ? 'Оригинал' : 'Original')
                      : (lang === 'ru' ? 'Тираж' : 'Edition')
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gallery-muted">
                    {lang === 'ru' ? 'Сертификат' : 'Certificate'}
                  </span>
                  <span className="text-gallery-text">
                    {artwork.certificate
                      ? (lang === 'ru' ? 'Есть' : 'Included')
                      : (lang === 'ru' ? 'Нет' : 'Not included')
                    }
                  </span>
                </div>
                {artwork.framed && (
                  <div className="flex justify-between">
                    <span className="text-gallery-muted">
                      {lang === 'ru' ? 'Оформление' : 'Framing'}
                    </span>
                    <span className="text-gallery-text">
                      {lang === 'ru' ? 'В раме' : 'Framed'}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {artwork.description && (
                <div className="mb-8 pb-8 border-b border-gallery-border">
                  <h3 className="text-gallery-text font-medium mb-3">
                    {lang === 'ru' ? 'Описание' : 'Description'}
                  </h3>
                  <p className="text-gallery-muted leading-relaxed">
                    {artwork.description[lang]}
                  </p>
                </div>
              )}

              {/* Price & CTA */}
              <div className="mb-8">
                {artwork.status === 'available' && artwork.price ? (
                  <>
                    <p className="text-3xl font-serif text-gallery-text mb-6">
                      EUR {artwork.price.toLocaleString()}
                    </p>
                    <div className="space-y-3">
                      <button
                        onClick={() => setShowInquiryForm(true)}
                        className="w-full flex items-center justify-center gap-3 bg-gallery-text text-white px-6 py-4 text-sm tracking-wider uppercase hover:bg-gallery-accent transition-colors"
                      >
                        <ShoppingBag size={18} />
                        {lang === 'ru' ? 'Купить' : 'Buy Now'}
                      </button>
                      <button
                        onClick={() => setShowInquiryForm(true)}
                        className="w-full flex items-center justify-center gap-3 border border-gallery-border text-gallery-text px-6 py-4 text-sm tracking-wider uppercase hover:border-gallery-accent transition-colors"
                      >
                        <MessageSquare size={18} />
                        {lang === 'ru' ? 'Запросить информацию' : 'Inquire'}
                      </button>
                    </div>
                  </>
                ) : artwork.status === 'sold' ? (
                  <div className="bg-gallery-light p-6 text-center">
                    <p className="text-gallery-text mb-2 font-medium">
                      {lang === 'ru' ? 'Работа продана' : 'This work has been sold'}
                    </p>
                    <p className="text-gallery-muted text-sm">
                      {lang === 'ru'
                        ? 'Свяжитесь с нами для заказа похожей работы'
                        : 'Contact us to commission a similar work'
                      }
                    </p>
                    <button
                      onClick={() => setShowInquiryForm(true)}
                      className="mt-4 text-gallery-accent hover:underline text-sm"
                    >
                      {lang === 'ru' ? 'Связаться' : 'Get in Touch'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-gallery-light p-6 text-center">
                    <p className="text-gallery-text mb-2 font-medium">
                      {lang === 'ru' ? 'Работа зарезервирована' : 'This work is reserved'}
                    </p>
                    <button
                      onClick={() => setShowInquiryForm(true)}
                      className="mt-2 text-gallery-accent hover:underline text-sm"
                    >
                      {lang === 'ru' ? 'Встать в лист ожидания' : 'Join Waiting List'}
                    </button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
                    isFavorite
                      ? 'border-gallery-accent text-gallery-accent'
                      : 'border-gallery-border text-gallery-muted hover:text-gallery-text'
                  }`}
                >
                  <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
                  <span className="text-sm">
                    {isFavorite
                      ? (lang === 'ru' ? 'В избранном' : 'Saved')
                      : (lang === 'ru' ? 'В избранное' : 'Save')
                    }
                  </span>
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 px-4 py-2 border border-gallery-border text-gallery-muted hover:text-gallery-text transition-colors"
                  >
                    <Share2 size={18} />
                    <span className="text-sm">
                      {lang === 'ru' ? 'Поделиться' : 'Share'}
                    </span>
                  </button>

                  {/* Share Menu */}
                  <AnimatePresence>
                    {showShareMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 mt-2 bg-white shadow-lg border border-gallery-border p-2 min-w-[180px] z-10"
                      >
                        <button
                          onClick={handleCopyLink}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gallery-muted hover:text-gallery-text hover:bg-gallery-bg transition-colors"
                        >
                          {copied ? <Check size={16} /> : <Copy size={16} />}
                          {copied
                            ? (lang === 'ru' ? 'Скопировано!' : 'Copied!')
                            : (lang === 'ru' ? 'Копировать ссылку' : 'Copy Link')
                          }
                        </button>
                        <a
                          href={`mailto:?subject=${artwork.title[lang]}&body=${window.location.href}`}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gallery-muted hover:text-gallery-text hover:bg-gallery-bg transition-colors"
                        >
                          <Mail size={16} />
                          {lang === 'ru' ? 'Email' : 'Email'}
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white border-t border-gallery-border">
          <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center">
              {prevArtwork ? (
                <Link
                  to={`/artwork/${prevArtwork.id}`}
                  className="flex items-center gap-4 group"
                >
                  <ArrowLeft size={20} className="text-gallery-muted group-hover:text-gallery-text transition-colors" />
                  <div>
                    <p className="text-xs text-gallery-muted uppercase tracking-wider">
                      {lang === 'ru' ? 'Предыдущая' : 'Previous'}
                    </p>
                    <p className="font-serif text-gallery-text group-hover:text-gallery-accent transition-colors">
                      {prevArtwork.title[lang]}
                    </p>
                  </div>
                </Link>
              ) : <div />}

              {nextArtwork ? (
                <Link
                  to={`/artwork/${nextArtwork.id}`}
                  className="flex items-center gap-4 text-right group"
                >
                  <div>
                    <p className="text-xs text-gallery-muted uppercase tracking-wider">
                      {lang === 'ru' ? 'Следующая' : 'Next'}
                    </p>
                    <p className="font-serif text-gallery-text group-hover:text-gallery-accent transition-colors">
                      {nextArtwork.title[lang]}
                    </p>
                  </div>
                  <ArrowRight size={20} className="text-gallery-muted group-hover:text-gallery-text transition-colors" />
                </Link>
              ) : <div />}
            </div>
          </div>
        </div>

        {/* Related Works */}
        {relatedArtworks.length > 0 && (
          <div className="bg-gallery-bg py-16">
            <div className="container mx-auto px-4">
              <h2 className="font-serif text-2xl md:text-3xl text-gallery-text text-center mb-12">
                {lang === 'ru' ? 'Похожие работы' : 'Related Works'}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {relatedArtworks.map((related) => (
                  <Link
                    key={related.id}
                    to={`/artwork/${related.id}`}
                    className="group"
                  >
                    <div className="bg-gallery-light overflow-hidden mb-3">
                      <div className="aspect-[4/5]">
                        <img
                          src={related.image}
                          alt={related.title[lang]}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <h3 className="font-serif text-gallery-text group-hover:text-gallery-accent transition-colors text-sm">
                      {related.title[lang]}
                    </h3>
                    <p className="text-gallery-muted text-xs">
                      {related.year}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Viewer */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          >
            {/* Controls */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 1}
                className="p-3 bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomOut size={20} />
              </button>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                className="p-3 bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={() => {
                  setIsFullscreen(false);
                  setZoomLevel(1);
                }}
                className="p-3 bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Navigation */}
            {prevArtwork && (
              <button
                onClick={() => navigate(`/artwork/${prevArtwork.id}`)}
                className="absolute left-4 p-4 bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
            )}
            {nextArtwork && (
              <button
                onClick={() => navigate(`/artwork/${nextArtwork.id}`)}
                className="absolute right-4 p-4 bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                <ArrowRight size={24} />
              </button>
            )}

            {/* Image */}
            <motion.div
              className="overflow-auto max-h-screen max-w-screen p-8"
              style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
            >
              <motion.img
                src={artwork.image}
                alt={artwork.title[lang]}
                className="max-h-[90vh] max-w-[90vw] object-contain"
                animate={{ scale: zoomLevel }}
                transition={{ duration: 0.2 }}
              />
            </motion.div>

            {/* Info */}
            <div className="absolute bottom-4 left-4 text-white">
              <p className="font-serif text-xl">{artwork.title[lang]}</p>
              <p className="text-white/70 text-sm">{artistInfo.name[lang]}, {artwork.year}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inquiry Modal */}
      <AnimatePresence>
        {showInquiryForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowInquiryForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="font-serif text-2xl text-gallery-text">
                    {lang === 'ru' ? 'Запрос на приобретение' : 'Purchase Inquiry'}
                  </h3>
                  <p className="text-gallery-muted text-sm mt-1">
                    {artwork.title[lang]}
                  </p>
                </div>
                <button
                  onClick={() => setShowInquiryForm(false)}
                  className="text-gallery-muted hover:text-gallery-text"
                >
                  <X size={24} />
                </button>
              </div>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm text-gallery-text mb-1">
                    {lang === 'ru' ? 'Имя' : 'Name'} *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gallery-border focus:outline-none focus:border-gallery-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gallery-text mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 border border-gallery-border focus:outline-none focus:border-gallery-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gallery-text mb-1">
                    {lang === 'ru' ? 'Телефон' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-gallery-border focus:outline-none focus:border-gallery-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gallery-text mb-1">
                    {lang === 'ru' ? 'Сообщение' : 'Message'}
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 border border-gallery-border focus:outline-none focus:border-gallery-accent resize-none"
                    placeholder={lang === 'ru'
                      ? 'Расскажите о вашем интересе к этой работе...'
                      : 'Tell us about your interest in this work...'
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gallery-text text-white py-4 text-sm tracking-wider uppercase hover:bg-gallery-accent transition-colors"
                >
                  {lang === 'ru' ? 'Отправить запрос' : 'Send Inquiry'}
                </button>
                <p className="text-gallery-muted text-xs text-center">
                  {lang === 'ru'
                    ? 'Мы ответим вам в течение 24 часов'
                    : 'We will respond within 24 hours'
                  }
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ArtworkDetailPage;
